import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import svgIcons from "../../../assets/svg/svgIcons";
import { useEventGetVideoDetailsMutation } from "../../../api/network/vmApiService";
import { DeviceSnapshotItem, VideoDetail } from "../../../api/types/Camera";
import { ALL_CAMERA_CLIP_TYPES } from "../../camera-admin/constants";
import AppVideoElement from "../../camera-admin/components/videoElement";
import { IncidentDetailResponse } from "./type";
import { toast } from "react-toastify";

type IncidentDetailVideoProps = {
  incident: IncidentDetailResponse;
  printMode?: boolean;
  onLoadComplete?: () => void;
};

// human label for a video's capture_mode (front / in-cabin / AUX / side-by-side),
// falling back to the raw mode or a positional label when unknown
const captureModeLabel = (mode: string, index: number) =>
  ALL_CAMERA_CLIP_TYPES.find((clip) => clip.value === mode)?.label ||
  mode ||
  `Camera ${index + 1}`;

const IncidentDetailVideo: FC<IncidentDetailVideoProps> = ({ incident, printMode = false, onLoadComplete }) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.incident.incident_review.detailsPage.widget_video",
  });
  const [playAttempted, setPlayAttempted] = useState<number | null>(null);
  const [videos, setVideos] = useState<VideoDetail[]>([]);
  const [snapshots, setSnapshots] = useState<DeviceSnapshotItem[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [fetchFailed, setFetchFailed] = useState(false);
  const hasCalledLoadComplete = useRef(false);
  const [videoFetchComplete, setVideoFetchComplete] = useState(false);
  const [getVideoDetails, { isLoading }] = useEventGetVideoDetailsMutation();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!incident.has_video || !incident.video_ticket) return;
    if (!printMode) {
      hasCalledLoadComplete.current = false;
      setFetchFailed(false);
      setVideoFetchComplete(false);
      setVideos([]);
      setSnapshots([]);
      setSelectedIndex(0);
      getVideoDetails({ ticketId: incident.video_ticket })
        .unwrap()
        .then((res) => {
          const fetchedVideos = (res?.data?.videos || []).filter((v) => !!v?.video_url);
          if (fetchedVideos.length > 0) {
            setVideos(fetchedVideos);
          }
          // some events (e.g. parked/still triggers) never record video and only
          // upload snapshot images - fall back to those instead of erroring out
          const fetchedSnapshots = (res?.data?.snapshots || []).filter((s) => !!s?.url);
          if (fetchedSnapshots.length > 0) setSnapshots(fetchedSnapshots);
          else setFetchFailed(true);
        })
        .catch(() => setFetchFailed(true));
    }
  }, [printMode]);

  const onPlayClick = (index:number) => {
    setPlayAttempted(index);
  };

  const onRefreshVideo=(index:number) =>{
    if (playAttempted === index) {
        const video = videoRef.current;
        if (video) {
            video.currentTime = 0;
            video.play();
        }
        return;
    }
    else
      onPlayClick(index);
  }
  const hasVideos = videos.length > 0;
  const hasSnapshots = snapshots.length > 0;
  const clips: { key: string; clipType: string; captureMode: string; url: string }[] = [
    ...videos.map((v, i) => ({ key: `${v.video_id}-${i}`, clipType: "video", captureMode: v.capture_mode, url: v.video_url })),
    ...snapshots.map((s, i) => ({ key: `${s.ticket}-${i}`, clipType: "snapshot", captureMode: s.capture_mode, url: s.url }))
  ]

  const selectedClip = clips[selectedIndex];

  useEffect(() => {
    if (!printMode)
      return;
    if (!incident.has_video || !incident.video_ticket) {
      setVideoFetchComplete(true);
      return;
    }

    getVideoDetails({
      ticketId: incident.video_ticket,
    })
      .unwrap()
      .then((res) => {
        const fetchedVideos = (res?.data?.videos || [])
          .filter(v => !!v?.video_url);
        const fetchedSnapshots = (res?.data?.snapshots || [])
          .filter(s => !!s?.url);
        setVideos(fetchedVideos);
        setSnapshots(fetchedSnapshots);
      })
      .catch((error) => {
        console.error("Failed to fetch video details", error);
      })
      .finally(() => {
        setVideoFetchComplete(true); // fires whether success or failure
      });
  }, [printMode, incident.has_video, incident.video_ticket]);

  useEffect(() => {
    if (printMode && videoFetchComplete && !hasCalledLoadComplete.current) {
      onLoadComplete?.();
      hasCalledLoadComplete.current = true;
    }
  }, [printMode, videoFetchComplete, onLoadComplete]);

  const renderContent = () => {
    if (!incident.has_video) {
      return <p className="text-white/90 text-sm px-8 text-center">{t("no_video")}</p>;
    }
    if (isLoading) {
      return <p className="text-white/90 text-sm px-8 text-center">{t("loading")}</p>;
    }
    if (fetchFailed) {
      toast.error(t("fetch_error") + " " + t("retry"))
    }
    if (!selectedClip) {
      return <p className="text-white/90 text-sm px-8 text-center">{t("no_video")}</p>;
    }
    if (selectedClip.clipType === "video") {
      if (playAttempted!==selectedIndex) {
        return (
          <button type="button" className="flex flex-col items-center gap-2 text-white" onClick={()=>onPlayClick(selectedIndex)}>
            <img src={svgIcons.PlayButton} alt={t("play")} className="size-14 drop-shadow" />
            <span className="text-sm">{t("play")}</span>
          </button>
        );
      }
      else return (
        <AppVideoElement
          key={selectedClip.url}
          ref={videoRef}
          controls
          autoPlay
          muted
          playsInline
          fixedHeightClass="h-52"
          className="w-full h-full object-contain"
          src={selectedClip.url}
        />
      );
    }
    if (selectedClip.clipType === "snapshot") {
      return (
        <>
          <div className="relative">
            <button
              type="button"
              onClick={() => setIsFullscreen(true)}
              className="flex justify-center items-center"
            >
              <img key={selectedClip.url} src={selectedClip.url} alt={t("heading")} className="w-full h-full object-contain" />
              <span className="absolute bottom-2 right-2 p-2 bg-black/50 text-white rounded hover:bg-black/70">
                <img src={svgIcons.FullScreenIcon} alt="full-screen" />
              </span>
            </button>
          </div>
          {isFullscreen && (
            <div
              className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center"
              onClick={() => setIsFullscreen(false)}
            >
              <img
                src={selectedClip.url}
                alt="Fullscreen preview"
                className="max-w-[95vw] max-h-[95vh] object-contain"
                onClick={(e) => e.stopPropagation()}
              />
              <button
                type="button"
                onClick={() => setIsFullscreen(false)}
                className="absolute top-6 right-4 p-2 text-white text-2xl"
              >
                ✕
              </button>
            </div>
          )}
        </>);
    }
    return <p className="text-white/90 text-sm px-8 text-center">{t("no_video")}</p>;
  };

  if (printMode && incident.has_video && (hasVideos || hasSnapshots)) {
    return (
      <div className="bg-white w-full mt-8 rounded-xl border border-heading-gray888/20 shadow-sm p-4">
        <h2 className="font-semibold text-xl text-heading-black">{t("heading")}</h2>
        {clips.map((clip, index) => (
          <div key={index} className="flex flex-col flex-wrap min-w-0 my-4">
            <span className="font-semibold">{clip.clipType.charAt(0).toUpperCase() + clip.clipType.slice(1)} URL: </span>
            <a
              href={clip.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline hover:text-blue-800 break-all"
            >
              {clip.url}
            </a>
          </div>
        ))}
      </div>
    )
  }
  else return (
    <div className="no-print flex flex-col bg-white rounded-xl border border-heading-gray888/20 shadow-sm p-4">
      <div className="flex items-center justify-between mb-3 pr-10">
        <h2 className="font-semibold text-[15px] text-heading-black">{t("heading")}</h2>
        <div className="flex items-center gap-2">
          <span
            className={`flex items-center justify-center size-7 rounded-full transition ${incident.has_video && (hasVideos || hasSnapshots || fetchFailed) && (selectedClip.clipType==="video")
              ? "hover:bg-accent-blue-pale cursor-pointer"
              : "opacity-40 cursor-not-allowed"
              }`}
            onClick={() => {
              if (incident.has_video && (hasVideos || hasSnapshots || fetchFailed)) onRefreshVideo(selectedIndex);
            }}
          >
            <img src={svgIcons.RefreshIcon} alt="" className="size-4 opacity-50" />
          </span>
        </div>
      </div>

      {/* camera toggle - only when the event returned more than one clip (video or snapshot) */}
      {clips.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {clips.map((clip, index) => {
            const isActive = index === selectedIndex;
            return (
              <button
                key={clip.key}
                type="button"
                onClick={() => setSelectedIndex(index)}
                className={`flex flex-row justify-center items-center h-7 px-3 rounded-md border text-xs font-semibold transition whitespace-nowrap ${isActive
                  ? "bg-accent-blue-dark border-accent-blue-dark text-white"
                  : "border-accent-blue-dark text-accent-blue-dark hover:bg-accent-blue-pale"
                  }`}
              >
                <img
                  src={clip.clipType === "video" ? svgIcons.VideoCameraIcon : svgIcons.ShowImageIconBlue}
                  className={`pr-1 size-5 ${isActive ? "brightness-0 invert" : ""}`} />
                {captureModeLabel(clip.captureMode, index)}
              </button>
            );
          })}
        </div>
      )}
      <div className="flex-1 min-h-52 rounded-lg bg-gray-900 flex items-center justify-center relative overflow-hidden">
        {renderContent()}
      </div>
    </div>
  );
};

export default IncidentDetailVideo;
